<!--
  FilterFieldsDropdown.vue

  Dropdown for selecting fields for filter conditions.
  Shows all filterable fields organized by group when available.

  Two modes:
  - Add mode (default): Shows "Filter by" label with filter icon
  - Select mode (when `selected` prop provided): Shows selected field label
-->
<template>
  <ToolbarDropdown v-slot="{ close }" :icon="icon" :label="label" width="220px" size="small">
    <!-- Grouped fields (when available) -->
    <template v-if="fieldGroups">
      <div v-for="group in fieldGroups" :key="group.key" class="fields-section">
        <div class="section-label text-7">{{ group.label }}</div>
        <div
          v-for="field in group.fields"
          :key="field.key"
          class="dropdown-item field-item text-7 pl-2 pr-3 py-1 g-1"
          :class="{ 'is-selected': field.key === selected }"
          @click="($emit('select', field), close())"
        >
          <font-awesome-icon :icon="getFieldIcon(field)" class="field-icon" />
          <span class="grow">{{ field.label }}</span>
          <font-awesome-icon v-if="field.key === selected" icon="check" class="check-icon" />
        </div>
      </div>
    </template>

    <!-- Flat field list (fallback) -->
    <div v-else-if="fields.length > 0" class="fields-section">
      <div class="section-label text-7">Fields</div>
      <div
        v-for="field in fields"
        :key="field.key"
        class="dropdown-item field-item text-7 pl-2 pr-3 py-1 g-1"
        :class="{ 'is-selected': field.key === selected }"
        @click="($emit('select', field), close())"
      >
        <font-awesome-icon :icon="getFieldIcon(field)" class="field-icon" />
        <span class="grow">{{ field.label }}</span>
        <font-awesome-icon v-if="field.key === selected" icon="check" class="check-icon" />
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="dropdown-item text-7 text-muted">No filterable fields available</div>
  </ToolbarDropdown>
</template>

<script setup lang="ts" generic="F extends FilterFieldOption">
import { computed } from "vue"
import ToolbarDropdown from "@auger/ui/components/ToolbarDropdown.vue"
import type { FieldKey } from "@auger/ui/composables/useCollection"

export interface FilterFieldOption {
  key: FieldKey
  label?: string
  icon?: string
  type?: string
  /** What the trigger reads once this field is selected, when it differs from `label`. */
  triggerLabel?: string
}

const fieldTypeIcons: Record<string, string> = {
  string: "font",
  number: "hashtag",
  boolean: "toggle-on",
  date: "calendar",
  select: "list",
  tag: "tag",
  record: "link",
  link: "link",
  user: "user",
  contact: "address-book",
  asset: "image",
  reference: "arrow-right",
  relationship: "share-nodes",
  schema: "layer-group",
  type: "shapes",
}

const props = withDefaults(
  defineProps<{
    fields?: F[]
    fieldGroups?: { key: string; label: string; fields: F[] }[] | null
    selected?: string | null
    addLabel?: string
    addIcon?: string
  }>(),
  {
    fields: () => [],
    fieldGroups: null,
    selected: null,
    addLabel: "Filter by",
    addIcon: "filter",
  }
)

defineEmits<{
  select: [field: F]
}>()

function findField(key: FieldKey): F | null {
  const fromFields = props.fields.find((f) => f.key === key)
  if (fromFields) return fromFields

  if (props.fieldGroups) {
    for (const group of props.fieldGroups) {
      const found = group.fields.find((f) => f.key === key)
      if (found) return found
    }
  }
  return null
}

function getFieldIcon(field: F | null) {
  if (field?.icon) return field.icon
  return fieldTypeIcons[field?.type ?? ""] || "circle"
}

const label = computed(() => {
  if (props.selected) {
    const field = findField(props.selected)
    return field?.triggerLabel || field?.label || "Select field"
  }
  return props.addLabel
})

const icon = computed(() => {
  return props.selected ? getFieldIcon(findField(props.selected)) : props.addIcon
})
</script>

<style>
.fields-section {
  .section-label {
    font-weight: 600;
    text-transform: uppercase;
    color: var(--auger-text-weak);
    padding: 4px 12px;
    text-wrap: nowrap;
  }
}

.field-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--auger-bg-muted);
  }

  &.is-selected {
    background: var(--auger-primary-soft);
    font-weight: 500;
  }

  .field-icon {
    width: 14px;
    color: var(--auger-text-weak);
  }

  .check-icon {
    width: 12px;
    color: var(--auger-primary);
  }
}
</style>
